import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from 'react-i18next';
import {
  ShieldCheck, LayoutDashboard, FileText,
  CreditCard, Settings, LogOut, Plus, Menu, X, Key, BarChart3, Globe, CalendarClock, MailWarning, Loader2, Briefcase, Palette
} from 'lucide-react';
import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = 'https://cyberguard-api-ka8f.onrender.com';

function EmailBanner({ email, token }) {
  const [sending, setSending] = useState(false);
  const resend = async () => {
    setSending(true);
    try {
      const res = await axios.post(`${API_URL}/api/auth/resend-verification`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(res.data.message || 'Email envoyé');
    } catch (err) {
      toast.error(err.response?.status === 429 ? 'Trop de demandes, réessayez dans une heure' : (err.response?.data?.detail || 'Envoi impossible'));
    } finally {
      setSending(false);
    }
  };
  return (
    <div className="border-b border-amber-500/30 bg-amber-500/5 px-6 lg:px-8 py-3 flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4" data-testid="email-banner">
      <p className="text-amber-200 text-sm flex items-center gap-2 flex-1">
        <MailWarning className="w-4 h-4 shrink-0" />
        <span>Confirmez votre adresse <strong>{email}</strong> avec le lien reçu par email pour lancer votre audit gratuit.</span>
      </p>
      <button onClick={resend} disabled={sending} className="inline-flex items-center gap-2 text-amber-300 hover:text-white font-mono text-xs uppercase shrink-0 disabled:opacity-50">
        {sending && <Loader2 className="w-3 h-3 animate-spin" />} Renvoyer l'email
      </button>
    </div>
  );
}
import LanguageSwitcher from './LanguageSwitcher';

export default function DashboardLayout({ children }) {
  const { user, token, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const plan = user?.subscription_plan || 'free';
  const isEnterprise = plan === 'enterprise';

  const navItems = [
    { icon: LayoutDashboard, label: t('sidebar.dashboard'), path: '/dashboard' },
    { icon: Plus,            label: t('sidebar.new_audit'), path: '/audits/new' },
    { icon: FileText,        label: t('sidebar.reports'),   path: '/reports' },
    { icon: Globe,           label: t('sidebar.domains'),   path: '/domains' },
    { icon: CalendarClock,   label: t('sidebar.monitoring'), path: '/surveillance' },
    { icon: CreditCard,      label: t('sidebar.billing'),   path: '/billing' },
    { icon: Settings,        label: t('sidebar.settings'),  path: '/settings' },
    ...(isEnterprise ? [
      { icon: Briefcase, label: t('sidebar.clients'), path: '/clients' },
      { icon: Palette, label: t('sidebar.branding'), path: '/agence' },
      { icon: Key, label: t('sidebar.api_keys'), path: '/developer/keys', badge: 'API' },
    ] : []),
    ...(user?.is_admin ? [{ icon: BarChart3, label: 'Admin', path: '/admin' }] : []),
  ];

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const getPlanBadgeColor = (plan) => {
    switch (plan) {
      case 'starter':    return 'bg-blue-500/20 text-blue-400 border-blue-500/30';
      case 'pro':        return 'bg-green-500/20 text-green-400 border-green-500/30';
      case 'enterprise': return 'bg-purple-500/20 text-purple-400 border-purple-500/30';
      default:           return 'bg-zinc-500/20 text-zinc-400 border-zinc-500/30';
    }
  };

  const planLabel = { free: 'Gratuit', starter: 'Starter', pro: 'Pro', enterprise: 'Enterprise' }[plan] || 'Gratuit';

  return (
    <div className="min-h-screen bg-[#09090b] flex">
      {sidebarOpen && (
        <div className="fixed inset-0 bg-black/50 z-40 lg:hidden" onClick={() => setSidebarOpen(false)} />
      )}

      <aside className={`
        fixed lg:static inset-y-0 left-0 z-50
        w-64 bg-zinc-900/80 border-r border-zinc-800
        transform transition-transform duration-200 ease-in-out
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
      `}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="h-16 flex items-center justify-between px-4 border-b border-zinc-800">
            <Link to="/dashboard" className="flex items-center gap-2">
              <ShieldCheck className="w-8 h-8 text-blue-500" />
              <span className="font-mono text-lg font-bold text-white">CyberGuard</span>
            </Link>
            <button className="lg:hidden text-zinc-400" onClick={() => setSidebarOpen(false)}>
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* User Info */}
          <div className="p-4 border-b border-zinc-800">
            <div className="flex items-center gap-3">
              {user?.picture ? (
                <img src={user.picture} alt={user.name} className="w-10 h-10 rounded-full" />
              ) : (
                <div className="w-10 h-10 rounded-full bg-blue-500/20 flex items-center justify-center">
                  <span className="text-blue-500 font-bold">{user?.name?.charAt(0).toUpperCase() || '?'}</span>
                </div>
              )}
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-white truncate">{user?.name || 'Utilisateur'}</div>
                <div className={`inline-flex px-2 py-0.5 text-xs rounded border mt-1 ${getPlanBadgeColor(plan)}`}>
                  {planLabel}
                </div>
              </div>
            </div>
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-4 space-y-1">
            {navItems.map((item) => {
              const isActive = location.pathname === item.path ||
                (item.path !== '/dashboard' && location.pathname.startsWith(item.path));
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setSidebarOpen(false)}
                  className={`flex items-center gap-3 px-3 py-2.5 rounded-sm transition-colors ${isActive ? 'bg-blue-500/10 text-blue-400' : 'text-zinc-400 hover:text-white hover:bg-zinc-800'}`}
                >
                  <item.icon className="w-5 h-5" strokeWidth={1.5} />
                  <span className="text-sm font-medium">{item.label}</span>
                  {item.badge && (
                    <span className="ml-auto text-[9px] font-mono uppercase px-1.5 py-0.5 border border-purple-500/30 text-purple-400 rounded-sm">{item.badge}</span>
                  )}
                </Link>
              );
            })}
          </nav>

          {/* Language + Logout */}
          <div className="p-4 border-t border-zinc-800 space-y-3">
            <div className="flex items-center justify-between px-3">
              <span className="text-zinc-500 text-xs font-mono uppercase">Langue</span>
              <LanguageSwitcher variant="header" />
            </div>
            <button
              onClick={handleLogout}
              className="flex items-center gap-3 px-3 py-2.5 w-full rounded-sm text-zinc-400 hover:text-white hover:bg-zinc-800 transition-colors"
            >
              <LogOut className="w-5 h-5" strokeWidth={1.5} />
              <span className="text-sm font-medium">{t('sidebar.logout')}</span>
            </button>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="lg:hidden h-16 flex items-center justify-between px-4 border-b border-zinc-800 bg-zinc-900/80">
          <button onClick={() => setSidebarOpen(true)} className="text-zinc-400">
            <Menu className="w-6 h-6" />
          </button>
          <Link to="/dashboard" className="flex items-center gap-2">
            <ShieldCheck className="w-6 h-6 text-blue-500" />
            <span className="font-mono font-bold text-white">CyberGuard</span>
          </Link>
          <div className="w-6" />
        </header>
        {user && user.email_verified === false && <EmailBanner email={user.email} token={token} />}
        {user?.admin_needs_2fa && location.pathname !== '/settings' && (
          <div className="border-b border-purple-500/30 bg-purple-500/5 px-6 lg:px-8 py-3 text-purple-200 text-sm">
            Compte administrateur : la double authentification est obligatoire pour accéder à l'administration.{' '}
            <Link to="/settings" className="underline hover:text-white">L'activer dans Paramètres</Link>
          </div>
        )}
        <main className="flex-1 overflow-auto">
          <div className="p-6 lg:p-8">{children}</div>
        </main>
      </div>
    </div>
  );
}
